import React, {
    useContext,
    useEffect,
    useState,
} from "react";
import { useForm, Controller } from "react-hook-form";
import {
    CurrencyCode,
    CurrencyFlag,
    CurrencyForm,
    CurrencyWrapper,
    InputBox,
} from "../../../styles";
import ErrorMessage from "../../../../../../components/ErrorMessage";
import { BeneficiaryBox, Fees } from "./Fees";
import { Button } from "../../../../../../components/common/Button";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import Loader from "../../../../../../components/common/Loader";
import { getExchangeRate } from "../../../api";
import { NetworkErrorAlertContext } from "../../../../../../context/NetworkErrorAlert";
import { setSettlementDetails } from "../../../SettlementPayoutSlice";
import { InputPhone } from "../../../../../../components/common/InputPhone";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import { ghsPayoutMethods } from "../../../../../Wallets/const";
import { removeLastCharacter } from "../../../../../Wallets/utils";
import { settlementMethodCurrencyMap } from "../../../../../Settlements/utils";
import { usePopulateFormFields } from "../../../../../../app/hooks/usePopulateFormFields";
import { useParams } from "react-router-dom";
import { computeSettlementPayoutFee, blockInvalidDecimalAmountKey, parseDecimalAmount, sanitizeDecimalAmountInput, GHS_ACCOUNT_NUMBER_MAX_LENGTH, validateGhsPhoneNumber } from "../../../../../../utils/helpers";

interface Props {
    inputtedAmount: number;
    setInputtedAmount: (data: any) => void;
    beneFiciaryAmount: number;
    setBeneficiaryAmount: (data: number) => void;
    onNext: () => void;
}

type LocalPayoutType = {
    payoutMethod: string;
    amount?: number;
    beneficiaryAmount?: number;
    phoneNumber?: string;
    paymentMethod?: string;
};

const Local = ({
    inputtedAmount,
    setInputtedAmount,
    beneFiciaryAmount,
    setBeneficiaryAmount,
    onNext,
}: Props) => {
    const {
        handleSubmit,
        control,
        watch,
        formState: { errors },
        setValue
    } = useForm();
    const dispatch = useAppDispatch();
    const { currencyCode } = useParams();
    const capitalizedCurrencyCode = currencyCode?.toUpperCase() || "";

    const [loading, setLoading] = useState(false);
    const { wallets } = useAppSelector(
        (state) => state.wallets,
    );

    const { onShowAlert } = useContext(
        NetworkErrorAlertContext,
    );

    const walletBalance = wallets?.find(
        (wallet) => wallet.currency == capitalizedCurrencyCode,
    );
    const Balance = walletBalance?.settlementBalance;

    const getSavedSettlementDetails = useAppSelector((state) => state?.settlementPayout?.settlementDetails);

    const formData = watch();
    const amount = formData.amount;
    const minimumPayoutValue = 1000;

    useEffect(() => {
        if (amount >= 50) {
            setLoading(true);
            getExchangeRate({
                sourceCurrency: capitalizedCurrencyCode,
                amount: amount,
                destinationCurrency: capitalizedCurrencyCode,
            }).then((data) => {
                setLoading(false);
                setValue('beneficiaryAmount', data.amount)
            });
        }
    }, [amount]);

    useEffect(() => {
        if (formData.beneficiaryAmount && formData.beneficiaryAmount > Balance) {
            onShowAlert(
                "",
                "The amount is more than your settlement balance",
            );
        }
    }, [formData.beneficiaryAmount]);

    usePopulateFormFields<LocalPayoutType>(getSavedSettlementDetails, setValue);

    const selectedPayoutMethod = settlementMethodCurrencyMap[formData?.payoutMethod]

    const computedFee = computeSettlementPayoutFee(
        formData?.amount,
        formData?.payoutMethod,
        formData?.paymentMethod
    );

    const isGhsPayout = selectedPayoutMethod === "GHS";
    const isValidGhsPhoneNumber = validateGhsPhoneNumber(formData?.phoneNumber) === true;

    const onSubmit = () => {
        const data = {
            ...getSavedSettlementDetails,
            ...formData,
            processingFee: computedFee,
        };
        dispatch(setSettlementDetails(data));
        onNext();
    };

    return (
        <div>
            <Loader isLoading={loading} />
            {selectedPayoutMethod === "GHS" &&
                <>
                    <InputBox>
                        <Controller
                            name="phoneNumber"
                            control={control}
                            rules={{
                                required: "Sender's phone number is required",
                                validate: validateGhsPhoneNumber,
                            }}
                            render={({ field }) => (
                                <InputPhone
                                    label="Sender’s phone number"
                                    placeholder="Sender’s phone number"
                                    defaultCountry={removeLastCharacter(selectedPayoutMethod)}
                                    field={field}
                                    showPhoneCode={true}
                                    maxLength={GHS_ACCOUNT_NUMBER_MAX_LENGTH}
                                    hasError={!!errors?.phoneNumber}
                                />
                            )}
                        />
                        {errors.phoneNumber?.message && (
                            <ErrorMessage message={String(errors.phoneNumber.message)} />
                        )}
                    </InputBox>

                    <InputBox>
                        <Controller
                            name="paymentMethod"
                            control={control}
                            rules={{ required: "Select a payment method." }}
                            render={({ field }) => (
                                <CustomSelect
                                    {...field}
                                    $width="100%"
                                    lightBorder
                                    placeholder="Select payment method"
                                    options={ghsPayoutMethods}
                                />
                            )}
                        />
                    </InputBox>
                </>
            }

            <InputBox>
                <Controller
                    name="amount"
                    control={control}
                    rules={{
                        required: true,
                    }}
                    defaultValue={formData?.amount}
                    render={({ field }) => (
                        <CurrencyForm
                            width="width"
                            {...field}
                            controls={false}
                            placeholder="Amount"
                            onKeyDown={blockInvalidDecimalAmountKey}
                            onPaste={(event) => {
                                event.preventDefault();
                                const pastedValue = parseDecimalAmount(
                                    event.clipboardData.getData("text"),
                                );
                                field.onChange(pastedValue);
                                setValue("amount", pastedValue);
                            }}
                            onChange={(value) => {
                                const numericValue = parseDecimalAmount(value);
                                field.onChange(numericValue);
                                setValue("amount", numericValue);
                            }}
                            formatter={(value) => {
                                const sanitized = sanitizeDecimalAmountInput(value);
                                if (!sanitized) {
                                    return "";
                                }

                                const [integerPart, decimalPart] = sanitized.split(".");
                                const formattedInteger = integerPart.replace(
                                    /\B(?=(\d{3})+(?!\d))/g,
                                    ",",
                                );

                                return decimalPart !== undefined
                                    ? `${formattedInteger}.${decimalPart}`
                                    : formattedInteger;
                            }}
                            parser={(value) =>
                                sanitizeDecimalAmountInput(
                                    value?.replace(/\$\s?|(,*)/g, "") ?? "",
                                )
                            }
                            addonAfter={
                                <CurrencyWrapper>
                                    <CurrencyFlag
                                        className={`currency-flag currency-flag-${selectedPayoutMethod?.toLowerCase()}`}
                                    />
                                    <CurrencyCode className="curr-code">
                                        {selectedPayoutMethod}
                                    </CurrencyCode>
                                </CurrencyWrapper>
                            }
                        />
                    )}
                />
                <div className="error-top">
                    {errors.amount?.type === "required" && (
                        <ErrorMessage message="Field is required" />
                    )}
                </div>
                {formData?.amount < minimumPayoutValue && (
                    <ErrorMessage
                        message={`Minimum amount is ${selectedPayoutMethod} ${minimumPayoutValue.toLocaleString()}`}
                    />
                )}
            </InputBox>
            {formData.beneficiaryAmount ? (
                <Fees currencySymbol={capitalizedCurrencyCode} fee={computedFee} text="Processing fee" />
            ) : (
                ""
            )}

            <BeneficiaryBox currencySymbol={capitalizedCurrencyCode} amount={formData?.beneficiaryAmount || 0} />

            <Button
                theme="primary"
                label="Continue"
                height="48px"
                onClick={handleSubmit(onSubmit)}
                disabled={
                    formData.beneficiaryAmount > Balance ||
                    amount < minimumPayoutValue ||
                    (isGhsPayout && !isValidGhsPhoneNumber)
                }
            />
        </div>
    );
};

export default Local;
